import API from 'api.js';
import {store} from "store/index"

// Page validation for tours
export const validateTourForPage = (tourName, currentPath) => {
  const tourPageMap = {
    'navigation': [
      '/admin/dashboard',
    ],
    'dashboard': ['/admin/dashboard'],
    'practice': ['/admin/test-material'],
    'templates': ['/admin/template'],
    'prediction': ['/admin/monthly-prediction'],
    'mocks': ['/admin/mock', '/admin/pending', '/admin/mock-result'],
    'extensive_mocks': ['/admin/extensive/new-sectional-tests', '/admin/extensive-pending/new-sectional-tests', '/admin/results'],
    'secret_strategy_videos': ['/admin/videos'],
    'daily_feedback': ['/admin/daily-feedback', '/admin/daily-task', '/admin/success-plan'],
    'session': ['/admin/session'],
    'question_list_filters': ['/admin/questionList'],
    'practice_question_features': ['/admin/practice'],
  };
  
  const validPaths = tourPageMap[tourName];
  return validPaths ? validPaths.some(path => currentPath.includes(path)) : false;
};

// Global tour cleanup utility
export const cleanupAllTours = () => {
  const activeTours = document.querySelectorAll('.shepherd-element');
  activeTours.forEach(tourElement => {
    const shepherdTour = tourElement._shepherdTour;
    if (shepherdTour && shepherdTour.isActive()) {
      shepherdTour.cancel();
    }
  });
  
  // Also cleanup any shepherd modal overlays
  const overlays = document.querySelectorAll('.shepherd-modal-overlay-container');
  overlays.forEach(overlay => overlay.remove());
};

// Static tour configuration data
export const TOUR_DATA = {
  navigation: {
    tour_name: 'navigation',
    steps: [
      { 
        element: 'tour-dashboard',
        emoji: '🏠',
        title: 'Dashboard',
        description: 'View your overall progress, statistics, and recent activities on the dashboard.',
      },
      { 
        element: 'tour-practice',
        emoji: '📚',
        title: 'Practice Material',
        description: 'Access all practice questions organized by question types to improve your skills.',
      },
      { 
        element: 'tour-monthly-prediction',
        emoji: '🔮',
        title: 'Monthly Prediction',
        description: 'Get the latest monthly predictions and practice the most likely exam questions.',
      },
      { 
        element: 'tour-templates',
        emoji: '📝',
        title: 'Templates',
        description: 'Use proven templates to enhance your skills and achieve higher scores.',
      },
      { 
        element: 'tour-prediction-files',
        emoji: '📁',
        title: 'Prediction Files',
        description: 'Download prediction files for offline practice and preparation.',
      },
      { 
        element: 'tour-mocks',
        emoji: '📊',
        title: 'Mock Tests',
        description: 'Take mock tests to simulate real exam conditions.',
      },
      { 
        element: 'tour-extensive-mocks',
        emoji: '📈',
        title: 'Extensive Mock Tests',
        description: 'Practice with extensive mock tests for thorough preparation.',
      },
      { 
        element: 'tour-videos',
        emoji: '🎥',
        title: 'Secret Strategy Videos',
        description: 'Watch premium strategy videos to learn expert tips and techniques.',
      },
      { 
        element: 'tour-daily-feedback',
        emoji: '✍️',
        title: 'Daily Feedback',
        description: 'Get personalized feedback from instructors.',
      },
      { 
        element: 'tour-live-session',
        emoji: '🎓',
        title: 'Live Session',
        description: 'Join live coaching sessions with expert instructors.',
      },
      { 
        element: 'tour-notifications',
        emoji: '🔔',
        title: 'Notifications',
        description: 'Stay updated with important announcements and reminders.',
      },
      { 
        element: 'tour-help',
        emoji: '❓',
        title: 'Need Help',
        description: 'Get support and answers to your questions from our team.',
      },
      { 
        element: 'tour-subscriptions',
        emoji: '💳',
        title: 'Subscriptions',
        description: 'Manage your subscription plans and payment history.',
      },
      { 
        element: 'tour-profile',
        emoji: '👤',
        title: 'My Profile',
        description: 'Update your profile information and account settings.',
      }
    ]
  },
  dashboard: {
    tour_name: 'dashboard',
    steps: [
      { 
        element: 'tour-welcome',
        emoji: '👋',
        title: 'Welcome!',
        description: 'Welcome to your dashboard! Here you can view all your important notifications in one place.',
      },
      { 
        element: 'tour-stats',
        emoji: '📊',
        title: 'Statistics',
        description: 'Track your practice performance and improvement over time with detailed statistics.',
      },
      { 
        element: 'tour-practice-btn',
        emoji: '📝',
        title: 'My-Stats',
        description: 'Click here to view your statistics and track your overall progress.',
      },
      { 
        element: 'tour-exam-btn',
        emoji: '📝',
        title: 'Estimates Exam Score',
        description: 'View your estimated exam scores based on your recent performance.',
      },
      { 
        element: 'tour-tutorial-videos',
        emoji: '🎥',
        title: 'Tutorial Videos',
        description: 'Learn proven strategies and tips for better performance through videos.',
      }
    ]
  },
  practice: {
    tour_name: 'practice',
    steps: [
      { element: 'Speaking-Zone', emoji: '🎤', title: "Speaking Practice", description: 'Practice Speaking and improve your skills with AI-powered evaluation.' },
      { element: 'Writing-Zone', emoji: '✍️', title: "Writing Practice", description: 'Practice Writing and improve your skills with AI-powered evaluation.' },
      { element: 'Reading-Zone', emoji: '📖', title: "Reading Practice", description: 'Practice Reading and improve your skills with AI-powered evaluation.' },
      { element: 'Listening-Zone', emoji: '🎧', title: "Listening Practice", description: 'Practice Listening and improve your skills with AI-powered evaluation.' },
    ]
  },
  templates: {
    tour_name: 'templates',
    steps: [
      { element: 'view-template', emoji: '🔍', title: "Templates", description: 'Explore templates designed to enhance your skills.' },
    ]
  },
  prediction: {
    tour_name: 'prediction',
    steps: [
      { element: 'view-prediction', emoji: '🔍', title: "Prediction Files", description: 'View Prediction Files' },
    ]
  },
  mocks: {
    tour_name: 'mocks',
    steps: [
      { element: 'Mock-section', emoji: '🔍', title: "Mock Test Category", description: 'Browse mock tests by category.' },
      { element: 'Mock-view', emoji: '🔍', title: "Attempt Test", description: 'Click here to attempt a mock test' },
    ]
  },
  extensive_mocks: {
    tour_name: 'extensive_mocks',
    steps: [
      { element: 'extmockspeaking', emoji: '🔍', title: "Extensive Mock Test", description: 'Browse extensive mock tests by category.' },
      { element: 'Mock-view', emoji: '🔍', title: "Attempt Test", description: 'Click here to attempt a extensive mock test' },
    ]
  },
  secret_strategy_videos: {
    tour_name: 'strategy-video',
    steps: [
      { element: 'secretstrategyvideo', emoji: '🔍', title: "Introduction Video", description: 'This is the Introduction video of the course' },
      { element: 'speakingvideos', emoji: '🔍', title: "Speaking Videos", description: 'Click here to view speaking strategy videos' },
      { element: 'writingvideos', emoji: '🔍', title: "Writing Videos", description: 'Click here to view writing strategy videos' },
      { element: 'readingvideos', emoji: '🔍', title: "Reading Videos", description: 'Click here to view reading strategy videos' },
      { element: 'listeningvideos', emoji: '🔍', title: "Listening Videos", description: 'Click here to view listening strategy videos' },
      { element: 'final-tips', emoji: '🔍', title: "Final Tips", description: 'Click here to view final tips for the exam' },
    ]
  },
  daily_feedback: {
    tour_name: 'daily_feedback',
    steps: [
      { element: 'daily-task', emoji: '🔍', title: "Daily Task", description: 'Track your progress by category.' },
      { element: 'filter-date', emoji: '🔍', title: "Filter Date", description: 'Click here to view your past feedback.' },
      { element: 'daily-view', emoji: '🔍', title: "Daily View", description: 'View your feedback.' },
      { element: 'daily-edit', emoji: '🔍', title: "Set Target", description: 'Click here to set your daily target' }
    ]
  },
  session: {
    tour_name: 'session',
    steps: [
      { element: 'filter-date-session', emoji: '🔍', title: "Filter Data", description: 'Click here to view sessions by date.' },
      { element: 'view-session', emoji: '🔍', title: "View Session", description: 'Click here to view session details.' }
    ]
  },
  question_list_filters: {
    tour_name: 'question_list_filters',
    steps: [
      { 
        element: window.innerWidth <= 768 ? 'mobile-filter-orderby' : 'filter-orderby',
        emoji: '🔢',
        title: 'Order By Filter',
        description: 'Sort questions by newest or oldest first to organize your practice sessions.',
      },
      { 
        element : window.innerWidth <= 768 ? 'mobile-filter-complexity' : 'filter-complexity',
        emoji: '📊',
        title: 'Complexity Filter',
        description: 'Filter questions by difficulty level: Easy, Medium, or Difficult to match your skill level.',
      },
      { 
        element: window.innerWidth <=768 ? 'mobile-filter-practice-status' : 'filter-practice-status',
        emoji: '✅',
        title: 'Practice Status',
        description: 'View only attempted or not attempted questions to track your progress.',
      },
      { 
        element: window.innerWidth <= 768 ? 'mobile-filter-badge' : 'filter-badge',
        emoji: '🏷️',
        title: 'Badge Filter',
        description: 'Filter questions by color badges (Red, Green, Yellow) that you have marked.',
      }
    ]
  },
  practice_question_features: {
    tour_name: 'practice_question_features',
    steps: [
      { 
        element: 'practice-question-tag',
        emoji: '🏷️',
        title: 'Question Tag',
        description: 'Mark questions with color badges (Red, Green, Yellow) to organize your practice and track which questions need review.',
      },
      { 
        element: 'practice-time-spent',
        emoji: '⏱️',
        title: 'Time Spent',
        description: 'Track how much time you spend on each question to improve your time management skills.',
      },
      { 
        element: 'practice-attempted-count',
        emoji: '🔢',
        title: 'Attempt Counter',
        description: 'See how many times you have attempted this question to track your practice frequency.',
      },
      { 
        element: 'practice-report-btn',
        emoji: '🚩',
        title: 'Report Question',
        description: 'Found an issue with the question? Use this button to report problems to our team.',
      },
    ]
  },
};

let onboardingData = {
  visited_screens: {},
  screen_scores: {},
  is_onboarded: false
};

let dataFetchPromise = null;

// Fetch from backend and update
export const fetchOnboardingData = async () => {
  if (dataFetchPromise) return dataFetchPromise;
  
  dataFetchPromise = (async () => {
    try {
      const response = await API({ method: 'GET', url: 'onboarding' });
      console.log('🌐 Backend response:', response.data.visited_screens);
      
      onboardingData = response.data;
      localStorage.setItem('onboardingData', JSON.stringify(onboardingData));
      console.log('✅ Updated with backend data');
      
      return onboardingData;
    } catch (error) {
      console.error('Failed to fetch onboarding:', error);
      const cached = localStorage.getItem('onboardingData');
      if (cached) {
        try {
          onboardingData = JSON.parse(cached);
        } catch (e) {}
      }
      return onboardingData;
    }
  })();
  
  return dataFetchPromise;
};

// Save to backend
export const saveTourCompletion = async (tourName) => {
  const score = onboardingData.screen_scores[tourName];
  
  if (!score) {
    console.error(`No score found for tour: ${tourName}`);
    return;
  }

   const token = store.getState().token;
  console.log('🔑 Token exists:', !!token);
  console.log("Tokenn : ",token);
  try {
    await API({
      method: 'PUT',
      url: 'onboarding',
      data: { onboarding_flags: score }
    });
    
    onboardingData.visited_screens[tourName] = true;
    localStorage.setItem('onboardingData', JSON.stringify(onboardingData));
    console.log(`✅ Tour "${tourName}" completed`);
  } catch (error) {
    console.error('Failed to save tour:', error);
    const tokenAfter = store.getState().token;
    console.log('🔑 Token after error:', !!tokenAfter)
  }
};

export const isUserOnboarded = () => {
  return onboardingData.is_onboarded === true;
};

// Get region access from Redux
export const hasRegionAccess = () => {
  const state = store.getState();
  const regionAccess = state.user?.region_access;
  console.log('Region access :', regionAccess);
  return regionAccess !== false;
};

// Check if completed (async to ensure backend data is loaded)
export const isTourCompleted = async (tourName) => {
  await fetchOnboardingData();
  
  if (!hasRegionAccess()) {
    return true;
  }
  
  if (isUserOnboarded()) {
    return true;
  }
  
  return onboardingData.visited_screens[tourName] === true;
};

export const clearOnboardingData = () => {
  localStorage.removeItem('onboardingData');
  onboardingData = {
    visited_screens: {},
    screen_scores: {},
    is_onboarded: false
  };
  dataFetchPromise = null;
  console.log('🔄 Onboarding data cleared on logout');
};

// Reset
// export const resetAllTours = () => {
//   localStorage.removeItem('onboardingData');
//   onboardingData = { visited_screens: {}, screen_scores: {} };
//   console.log('🔄 All tours reset');
// };

// Get completed
export const getCompletedTours = () => {
  return Object.keys(onboardingData.visited_screens).filter(
    key => onboardingData.visited_screens[key]
  );
};
